import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames/bind";
import withTranslation from "../../translation";
import DeleteItem from "./DeleteItem";
import Price from "../Price";
import styles from "./index.css";
import formatImageUrl from "../../../helpers/formatImageUrl";
import { withParams } from "../../../../helpers/queryString";

const cx = classnames.bind(styles);

const getProductUrl = (images, size, type = "Standard1") => {
  const filteredImagesBySize = images.find(image => image.size === size);
  const filteredImagesByType = images.find(image => image.type === type);

  if (filteredImagesBySize) {
    return filteredImagesBySize.url;
  }

  if (filteredImagesByType) {
    return formatImageUrl(filteredImagesByType.url);
  }

  return null;
};

const containerClassNames = (itemType = "") =>
  cx("container", "item", itemType.toLowerCase());

const LineItem = ({
  colour: color,
  defaultStoreUrl,
  formatTranslation,
  id,
  productId,
  images,
  imageUrl,
  itemType,
  name,
  price,
  productUrl,
  quantity,
  size
}) => {
  const voucherUrl = `${defaultStoreUrl}gift-vouchers/?edit=true`;
  const itemUrl = itemType === "Voucher" ? voucherUrl : productUrl;

  const linkWithAnalytics = withParams(itemUrl, {
    ctaref: "mini bag",
    featureref1: "mini bag"
  });

  const productImage = getProductUrl(images, "l");
  const largeProductImage = getProductUrl(images, "xl");

  return (
    <div className={containerClassNames(itemType)}>
      <a href={linkWithAnalytics} className={styles.link} tabIndex="-1">
        {imageUrl && <img src={imageUrl} alt="" className={styles.image} />}
        {images.length > 0 && (
          <img
            src={productImage}
            srcSet={`${largeProductImage} 2x`}
            alt=""
            className={styles.image}
          />
        )}
        <dl className={styles.details}>
          <div className={styles.price}>
            <dt className={styles.srOnly}>
              {formatTranslation("MiniBag.Price")}
            </dt>
            <dd>
              <Price {...price} />
            </dd>
          </div>
          <div className={styles.name}>
            <dt className={styles.srOnly}>
              {formatTranslation("MiniBag.Name")}
            </dt>
            <dd>{name}</dd>
          </div>
          <div className={styles.meta}>
            {color && itemType === "Product" && (
              <div className={styles.color}>
                <dt className={styles.srOnly}>
                  {formatTranslation("MiniBag.Color")}
                </dt>
                <dd>{color}</dd>
              </div>
            )}
            {size && itemType === "Product" && (
              <div className={styles.size}>
                <dt className={styles.srOnly}>
                  {formatTranslation("MiniBag.Size")}
                </dt>
                <dd>{size}</dd>
              </div>
            )}
            <div className={styles.quantity}>
              <dt className={styles.term}>
                <abbr title={formatTranslation("MiniBag.Quantity")}>
                  {formatTranslation("MiniBag.Qty")}
                </abbr>
              </dt>
              <dd className={styles.quantity}>{quantity}</dd>
            </div>
          </div>
        </dl>
      </a>
      <DeleteItem id={id} productId={productId} />
    </div>
  );
};

LineItem.propTypes = {
  colour: PropTypes.string,
  formatTranslation: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  productId: PropTypes.number.isRequired,
  itemType: PropTypes.oneOf(["Product", "Voucher", "Subscription"]),
  imageUrl: PropTypes.string,
  images: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        size: PropTypes.string,
        url: PropTypes.string
      })
    ),
    PropTypes.arrayOf(
      PropTypes.shape({
        size: PropTypes.string,
        type: PropTypes.string
      })
    )
  ]),
  name: PropTypes.string,
  price: PropTypes.shape({
    current: PropTypes.shape({
      text: PropTypes.string
    }),
    isMarkedDown: PropTypes.bool,
    isOutletPrice: PropTypes.bool,
    previous: PropTypes.shape({
      text: PropTypes.string
    }),
    rrp: PropTypes.shape({
      text: PropTypes.string
    })
  }),
  productUrl: PropTypes.string,
  quantity: PropTypes.number,
  size: PropTypes.string,
  defaultStoreUrl: PropTypes.string.isRequired
};

LineItem.defaultProps = {
  images: []
};

export default withTranslation(LineItem);
